import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VideocamIcon from '@mui/icons-material/Videocam';
import { Box, IconButton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import type { CreativeAsset } from '@/entities/creatives';
import { CreativeAssetType } from '@/entities/creatives';

interface VideoItem {
  key: string;
  url: string;
  previewImageUrl: string | null;
}

function buildVideos(
  videoPreviewUrl: string,
  assets: CreativeAsset[],
): VideoItem[] {
  const videos: VideoItem[] = [];

  if (videoPreviewUrl) {
    videos.push({
      key: 'preview',
      url: videoPreviewUrl,
      previewImageUrl: null,
    });
  }

  for (const asset of assets) {
    if (asset.type === CreativeAssetType.Video && asset.url) {
      videos.push({
        key: asset.id,
        url: asset.url,
        previewImageUrl: asset.previewImageUrl,
      });
    }
  }

  return videos;
}

export function VideoCarousel({
  videoPreviewUrl,
  assets,
  resetKey,
}: {
  readonly videoPreviewUrl: string;
  readonly assets: CreativeAsset[];
  readonly resetKey: unknown;
}) {
  const videos = buildVideos(videoPreviewUrl, assets);
  const [index, setIndex] = useState(0);
  const hasMultipleVideos = videos.length > 1;

  useEffect(() => {
    setIndex(0);
  }, [resetKey]);

  if (videos.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No video preview available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {hasMultipleVideos && (
          <IconButton
            size="small"
            onClick={() => setIndex((i) => i - 1)}
            disabled={index === 0}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}
        <Box
          component="video"
          controls
          src={videos[index].url}
          sx={{
            flex: 1,
            minWidth: 0,
            width: '100%',
            display: 'block',
            borderRadius: 1,
          }}
        />
        {hasMultipleVideos && (
          <IconButton
            size="small"
            onClick={() => setIndex((i) => i + 1)}
            disabled={index === videos.length - 1}
          >
            <ChevronRightIcon />
          </IconButton>
        )}
      </Box>

      {hasMultipleVideos && (
        <>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 0.5,
              color: 'text.secondary',
            }}
          >
            {index + 1} / {videos.length}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              justifyContent: 'center',
              mt: 1,
              flexWrap: 'wrap',
            }}
          >
            {videos.map((video, i) => (
              <Box
                key={video.key}
                onClick={() => setIndex(i)}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 0.5,
                  cursor: 'pointer',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'action.hover',
                  opacity: i === index ? 1 : 0.45,
                  border: i === index ? '2px solid' : '2px solid transparent',
                  borderColor: i === index ? 'primary.main' : 'transparent',
                }}
              >
                {video.previewImageUrl ? (
                  <Box
                    component="img"
                    src={video.previewImageUrl}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <VideocamIcon fontSize="small" color="action" />
                )}
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}
